import React, { useState } from "react";
import { TableCell, Checkbox, TextField, SvgIcon, FormHelperText, Tooltip } from "@mui/material"; // <-- add Tooltip import
import { DtAutocomplete } from "../autocomplete";
import { IDataColumn, DTDataGridColumnType, DTDataGridAlignment } from "./dtDataGridTypes";

interface DtDataGridCellProps {
  row: any;
  col: IDataColumn;
  rowIndex: number;
  colIndex: number;
  isEditing: boolean;
  editRowData: any;
  setEditRowData: (data: any) => void;
  controlWidth?: number; // percent, optional
  errors?: Record<string, string | undefined>; // fieldId -> error message
  sx?: React.CSSProperties | Record<string, any>; // style override for TableCell
  pageConfig?: { page: number; pageSize: number }; // <-- add this line
}

const standardFontSize = 15;

const DtDataGridCell: React.FC<DtDataGridCellProps> = ({
  row,
  col,
  rowIndex,
  colIndex,
  isEditing,
  editRowData,
  setEditRowData,
  controlWidth,
  errors,
  sx,
  pageConfig, // <-- add this line
}) => {
  const cellKey = `cell-${rowIndex}-${colIndex}`;
  const isEditable =
    typeof col.editable === "function"
      ? col.editable(row)
      : !!col.editable;

  // TableCell width
  const widthPercent =
    typeof col.width === "number"
      ? `${Math.min(col.width, 100)}%`
      : undefined;
  const widthStyle: React.CSSProperties = widthPercent
    ? { width: widthPercent, boxSizing: "border-box" }
    : { boxSizing: "border-box" };

  // Merge styles: width and sx prop only (heightStyle removed)
  const mergedStyle = { ...widthStyle, fontSize: standardFontSize, ...(sx || {}) };

  // Control width (for edit/add mode)
  const controlWidthPercent =
    typeof controlWidth === "number"
      ? `${Math.min(controlWidth, 100)}%`
      : "100%";

  // Calculate alignment style
  const align: "left" | "right" | "center" =
    (col.alignment as DTDataGridAlignment) || "left";

  // Get error for this field
  const errorMsg = errors?.[col.fieldId];
  const isError = !!errorMsg;

  // Edit mode
  if (isEditing && isEditable) {
    if (col.datatype === "text") {
      return (
        <TableCell key={cellKey}  style={mergedStyle as React.CSSProperties}  align={align}>
          <TextField
            size="small"
            value={editRowData ? editRowData[col.fieldId] : ""}
            onChange={e =>
              setEditRowData((prev: any) => ({
                ...prev,
                [col.fieldId]: e.target.value,
              }))
            }
            fullWidth
            variant="outlined"
            placeholder={col.name || ""}
            disabled={col.disabled}
            error={isError}
            helperText={isError ? errorMsg : undefined}
            sx={{
              width: controlWidthPercent,
              fontSize: standardFontSize,
              ...(col.disabled
                ? {
                  fontWeight: 500,
                  color: "#3b3b3bff",
                  '& .MuiInputBase-root': {
                    fontSize: standardFontSize,
                    fontWeight: 500,
                  },
                }
                : {}),
            }}
            inputProps={{
              style: {
                width: "100%",
                padding: "15px 12px",
                boxSizing: "border-box",
                fontSize: standardFontSize,
              },
            }}
          />
        </TableCell>
      );
    }
    if (col.datatype === "checkbox") {
      return (
        <TableCell key={cellKey}  style={mergedStyle as React.CSSProperties}  align={align}>
          <Checkbox
            size="small"
            checked={!!(editRowData ? editRowData[col.fieldId] : false)}
            onChange={e =>
              setEditRowData((prev: any) => ({
                ...prev,
                [col.fieldId]: e.target.checked,
              }))
            }
            sx={{ p: 0, width: controlWidthPercent }}
          />
          {isError && (
            <FormHelperText error sx={{ fontSize: 12, ml: 0 }}>
              {errorMsg}
            </FormHelperText>
          )}
        </TableCell>
      );
    }
    if (col.datatype === "dropdownlist") {
      return (
        <TableCell key={cellKey} style={mergedStyle as React.CSSProperties} align={align}>
          <DtAutocomplete
            options={col.dataOptions || []}
            value={
              (col.dataOptions || []).find(
                opt => opt === (editRowData ? editRowData[col.fieldId] : "")
              ) || undefined
            }
            onChange={(_event, value) => {
              setEditRowData((prev: any) => {
                const updatedRow = {
                  ...prev,
                  [col.fieldId]: value ? value : "",
                };

                return {
                  ...updatedRow,
                  ...(col.calculate ? col.calculate(updatedRow) : {}),
                };
                
              });
            }}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                variant="outlined"
                fullWidth
                error={isError}
                helperText={isError ? errorMsg : undefined}
                sx={{
                  width: controlWidthPercent,
                  fontSize: standardFontSize,
                  '& .MuiInputBase-root': {
                    fontSize: standardFontSize,
                    padding: "4px !important",
                  },
                  '& .MuiInputBase-input': {
                    fontSize: "15px",
                    padding: '8px 12px',
                    boxSizing: 'border-box',
                    width: '100%',
                  },
                }}
                inputProps={{
                  ...params.inputProps,
                  style: {
                    ...params.inputProps?.style,
                    width: "100%",
                  },
                }}
              />
            )}
          />
        </TableCell>
      );
    }
    if (col.renderCell) {
      return (
        <TableCell key={cellKey}  style={mergedStyle as React.CSSProperties}  align={align}>
          {col.renderCell(editRowData, rowIndex)}
        </TableCell>
      );
    }
  }

  // Display mode
  if (col.renderCell) {
    const tooltipText = typeof col.tooltip === "function" ? col.tooltip(row) : col.tooltip;
    return (
      <TableCell key={cellKey} style={mergedStyle as React.CSSProperties} align={align}>
        {tooltipText ? (
          <Tooltip title={tooltipText} arrow enterDelay={300} leaveDelay={100}>
            <span style={{ width: "100%", display: "inline-block" }}>
              {col.renderCell(row, rowIndex, pageConfig)} {/* <-- pass pageConfig here */}
            </span>
          </Tooltip>
        ) : (
          col.renderCell(row, rowIndex, pageConfig)  
        )}
      </TableCell>
    );
  }
  if (col.datatype === "checkbox") {
    const tooltipText = typeof col.tooltip === "function" ? col.tooltip(row) : col.tooltip;
    return (
      <TableCell key={cellKey} style={mergedStyle as React.CSSProperties} align={align}>
        {tooltipText ? (
          <Tooltip title={tooltipText} arrow enterDelay={300} leaveDelay={100}>
            <span style={{ width: "100%", display: "inline-block" }}>
              <Checkbox size="small" checked={!!row[col.fieldId]} disabled sx={{ p: 0 }} />
            </span>
          </Tooltip>
        ) : (
          <Checkbox size="small" checked={!!row[col.fieldId]} disabled sx={{ p: 0 }} />
        )}
      </TableCell>
    );
  }
  if (col.datatype === "image" && col.displayImage) {
    const tooltipText = typeof col.tooltip === "function" ? col.tooltip(row) : col.tooltip;
    return (
      <TableCell key={cellKey} style={mergedStyle as React.CSSProperties} align={align}>
        {tooltipText ? (
          <Tooltip title={tooltipText} arrow enterDelay={300} leaveDelay={100}>
            <span style={{ width: "100%", display: "inline-block" }}>
              <SvgIcon component={col.displayImage} />
            </span>
          </Tooltip>
        ) : (
          <SvgIcon component={col.displayImage} />
        )}
      </TableCell>
    );
  }
  // Default text cell
  const tooltipText = typeof col.tooltip === "function" ? col.tooltip(row) : col.tooltip;
  const spanStyle = {
    width: "100%",
    display: "inline-block",
    whiteSpace: sx?.whiteSpace ?? "nowrap",
    overflow: sx?.overflow ?? "hidden",
    textOverflow: sx?.textOverflow ?? "ellipsis",
    verticalAlign: sx?.verticalAlign ?? "top",
    maxWidth: sx?.maxWidth ?? sx?.width ?? "100%",
  };

  return (
    <TableCell
      key={cellKey}
      align={align}
      sx={{ fontSize: standardFontSize, lineHeight: 1.5, letterSpacing: 0.6, ...(sx || {}) }}
    >
      {tooltipText ? (
        <Tooltip
          title={tooltipText}
          arrow
          enterDelay={300}
          leaveDelay={100}
          componentsProps={{
            tooltip: {
              sx: { fontSize: "1rem" }
            }
          }}
        >
          <span style={spanStyle}>
            {row[col.fieldId]}
          </span>
        </Tooltip>
      ) : (
        <span style={spanStyle}>
          {row[col.fieldId]}
        </span>
      )}
    </TableCell>
  );
};

export default DtDataGridCell;